import { useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { cx } from '@linaria/core';
import { useJSONModal } from 'modules/Predictions/components/PredictionSidebar/useUploadModal';
import { useMediaState } from 'modules/Canvas/contexts/MediaProvider/MediaState';
import { useMediaPlayback } from 'modules/Canvas/contexts/MediaProvider/MediaPlayback';
import { ShapePill } from 'components/ShapePill';
import { SearchInput } from 'components/ui_library/SearchInput';
import { Button } from 'components/ui_library/Button';
import { RectRegionThumb } from 'components/Canvas/RegionThumb';
import { IconTagMultiSelect, IconBoundingBox } from 'components/Icons';
import { createValidConceptName } from 'utils/concepts';
import useCapturedVideoFrame from 'components/ModelOutputRenderer/components/ModelOutputTypeViews/useCapturedVideoFrame';
import { CollapsibleBar, CollapsibleBarProps } from 'components/ModelOutputRenderer/components/CollapsibleBar/CollapsibleBar';
import { hasRegions, hasFrames, transformRegionData, hasBbox } from 'components/ModelOutputRenderer/components/utils';

interface ConceptWithRegionViewProps {
  model: CF.API.Models.Model;
  modelOutput: CF.API.ModelOutputs.ModelOutput;
  regionColorMap: Record<string, string>;
  collapsibleBarProps: Omit<CollapsibleBarProps, 'children' | 'isThresholdSliderVisible'>;
}

export const ConceptWithRegionView = observer(
  ({ model, modelOutput, regionColorMap, collapsibleBarProps }: ConceptWithRegionViewProps): JSX.Element | null => {
    const { data } = modelOutput;
    const minThreshold = collapsibleBarProps.minThreshold || 0;
    const mediaPlayback = useMediaPlayback();
    const currentSecond = mediaPlayback.currentTimeInSeconds;
    const frameVideo = useCapturedVideoFrame(data, currentSecond);

    const [filterString, setFilterString] = useState<string>(''); // Display Filter Concept only if output has concepts
    const mediaState = useMediaState();

    const openJsonModal = useJSONModal(
      {
        json: modelOutput,
      },
      'model_page',
    );

    const onFilterStringChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      setFilterString(createValidConceptName(e.target.value));
    };

    const conceptRegionMap = useMemo(() => {
      if (hasRegions(data) && data.regions) {
        return transformRegionData(data.regions);
      }
      if (hasFrames(data)) {
        const regionData = data.frames && data.frames?.[currentSecond]?.data;
        if (regionData && hasRegions(regionData) && regionData.regions) {
          return transformRegionData(regionData.regions);
        }
      }
      return {};
    }, [data, currentSecond]);

    const conceptsRegionView = Object.entries(conceptRegionMap)?.map(([concept, regions]) => {
      if (filterString && !concept.toLowerCase().includes(filterString.toLowerCase())) {
        return null;
      }
      const filteredRegions = regions.filter((region) => Number.parseFloat(region.conceptValue) > minThreshold);
      const conceptColor = regionColorMap[regions[0].id];
      const badgeColor = {
        border: `2px dashed ${conceptColor}`,
        color: conceptColor,
      };
      // NOTE: REGION CAN HAVE EITHER BBOX OR MASK
      // This component renders different views for models with bounding boxes and models with masks
      const regionHasBbox = !!filteredRegions.length && hasBbox(filteredRegions[0].regionInfo);

      return (
        <div className="concept-with-region">
          {regionHasBbox && (
            <div className="concept-header">
              <div className="concept-count-badge" style={badgeColor}>
                {filteredRegions.length}
              </div>
              <div className="concept-name" style={{ color: conceptColor }}>
                {concept}
              </div>
            </div>
          )}
          <div className={cx(regionHasBbox && 'region-wrapper')}>
            {filteredRegions.map((region) => {
              const regionInfo = region.regionInfo;
              const bbox = 'bounding_box' in regionInfo && regionInfo.bounding_box;
              return (
                <>
                  <div className="region-body concept">
                    {bbox ? (
                      <div className="region-thumbnail">
                        <IconBoundingBox size={18} color="#344054" />
                        <ShapePill
                          thumb={
                            <RectRegionThumb
                              id={region.id}
                              regionData={{ bbox, color: '#000' }}
                              imageData={mediaState?.imageData}
                              frameVideo={frameVideo}
                            />
                          }
                          className="thumbnail"
                        >
                          <RectRegionThumb
                            id={region.id}
                            regionData={{ bbox, color: '#000' }}
                            imageData={mediaState?.imageData}
                            frameVideo={frameVideo}
                          />
                        </ShapePill>
                      </div>
                    ) : (
                      <div className="concept-name">
                        <IconTagMultiSelect size={19} color="#344054" />
                        {concept}
                      </div>
                    )}
                    <div className="concept-footer">
                      <div className="concept-value">{region.conceptValue}</div>
                    </div>
                  </div>
                </>
              );
            })}
          </div>
        </div>
      );
    });

    return (
      <>
        {conceptRegionMap && (
          <CollapsibleBar modelId={model.id} isThresholdSliderVisible {...collapsibleBarProps}>
            <div className="filter-bar">
              <SearchInput placeholder="Filter by concept" onChange={onFilterStringChange} value={filterString} />
            </div>
            <div className="concepts">{conceptsRegionView}</div>
            <Button variant="secondary" size="sm" onClick={openJsonModal}>
              View JSON
            </Button>
          </CollapsibleBar>
        )}
      </>
    );
  },
);
